/**
 * @fileoverview Business Logic (BL) for the 'Rasca y gana' (Scratcher) game dynamic (Tile ID 43).
 * Encapsulates input validations, parameter transformations, and delegates calls to the Data Layer (DL).
 * 
 * @module utilities/BL/Scratcher
 */

import * as DataLayerScratcher from './../DL/Scratcher';

/**
 * Retrieves all visual themes configured for the Scratcher dynamic.
 * 
 * @async
 * @function GetThemes
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Response payload with theme list.
 */
export const GetThemes = async () => {
    return await DataLayerScratcher.GetThemes();
};

/**
 * Retrieves a single Scratcher theme by its unique ID.
 * 
 * @async
 * @function GetTheme
 * @param {number} themeIdentifier - Unique theme ID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload with theme data.
 */
export const GetTheme = async (themeIdentifier) => {
    if (!themeIdentifier || Number(themeIdentifier) <= 0) {
        return { success: false, message: 'El identificador del tema es inválido.' };
    }
    return await DataLayerScratcher.GetTheme(themeIdentifier);
};

/**
 * Validates and saves a Scratcher theme configuration.
 * 
 * @async
 * @function SaveTheme
 * @param {Object} themeData - Theme attributes and configuration.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload with saved theme.
 */
export const SaveTheme = async (themeData) => {
    if (!themeData || typeof themeData !== 'object') {
        return { success: false, message: 'La información del tema es obligatoria.' };
    }

    if (!themeData.name || themeData.name.trim() === '') {
        return { success: false, message: 'El nombre del tema es obligatorio.' };
    }

    return await DataLayerScratcher.SaveTheme(themeData);
};

/**
 * Deletes a Scratcher theme by ID after validating its presence.
 * 
 * @async
 * @function DeleteTheme
 * @param {number} themeIdentifier - Unique theme ID.
 * @returns {Promise<{success: boolean, message?: string}>} Response payload confirming deletion.
 */
export const DeleteTheme = async (themeIdentifier) => {
    if (!themeIdentifier || Number(themeIdentifier) <= 0) {
        return { success: false, message: 'El identificador del tema a eliminar es inválido.' };
    }
    return await DataLayerScratcher.DeleteTheme(themeIdentifier);
};

/**
 * Clones an existing Scratcher theme record.
 * 
 * @async
 * @function CloneTheme
 * @param {number} themeIdentifier - Source theme ID to clone.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload with cloned theme.
 */
export const CloneTheme = async (themeIdentifier) => {
    if (!themeIdentifier || Number(themeIdentifier) <= 0) {
        return { success: false, message: 'El identificador del tema a clonar es inválido.' };
    }
    return await DataLayerScratcher.CloneTheme(themeIdentifier);
};

/**
 * Retrieves the global Scratcher room configuration.
 * 
 * @async
 * @function GetConfig
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload with configuration.
 */
export const GetConfig = async () => {
    return await DataLayerScratcher.GetConfig();
};

/**
 * Saves the global Scratcher room configuration.
 * 
 * @async
 * @function SaveConfig
 * @param {Object} configurationData - Room configuration data.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload with saved configuration.
 */
export const SaveConfig = async (configurationData) => {
    if (!configurationData || typeof configurationData !== 'object') {
        return { success: false, message: 'Los datos de configuración son obligatorios.' };
    }
    return await DataLayerScratcher.SaveConfig(configurationData);
};

/**
 * Retrieves the historical log of past Scratcher matches.
 * 
 * @async
 * @function GetMatchesHistory
 * @param {number} [queryLimit=50] - Number of matches to fetch.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Response payload with match history.
 */
export const GetMatchesHistory = async (queryLimit = 50) => {
    return await DataLayerScratcher.GetMatchesHistory(queryLimit);
};

/**
 * Saves or persists a completed match record.
 * 
 * @async
 * @function SaveMatchResult
 * @param {Object} matchData - Match record payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload.
 */
export const SaveMatchResult = async (matchData) => {
    if (!matchData || typeof matchData !== 'object') {
        return { success: false, message: 'Los datos de la partida son requeridos.' };
    }
    return await DataLayerScratcher.SaveMatchResult(matchData);
};

/**
 * Packs a single file into FormData and uploads it to the backend with SkiaSharp optimization.
 * 
 * @async
 * @function UploadMedia
 * @param {File|Blob} fileObject - Media file to upload.
 * @param {string} [mediaCategory=''] - Optional category (avatar, winner, background, cover, hidden, tool, logo).
 * @returns {Promise<{success: boolean, url?: string, message?: string}>} Upload result with file URL.
 */
export const UploadMedia = async (fileObject, mediaCategory = '') => {
    if (!fileObject) {
        return { success: false, message: 'No se ha seleccionado ningún archivo.' };
    }

    const multipartFormData = new FormData();
    multipartFormData.append('file', fileObject);
    if (mediaCategory) {
        multipartFormData.append('type', mediaCategory);
    }

    return await DataLayerScratcher.UploadMedia(multipartFormData);
};

/**
 * Packs an array of files into FormData and uploads them in batch with server optimization.
 * 
 * @async
 * @function UploadBatchMedia
 * @param {Array<File|Blob>|FileList} filesCollection - Array or FileList of media files.
 * @param {string} [mediaCategory=''] - Category for optimization.
 * @returns {Promise<{success: boolean, urls?: Array<string>, count?: number, message?: string}>} Batch result.
 */
export const UploadBatchMedia = async (filesCollection, mediaCategory = '') => {
    if (!filesCollection || filesCollection.length === 0) {
        return { success: false, message: 'No se han proporcionado archivos para la carga múltiple.' };
    }

    const multipartFormData = new FormData();
    for (let fileIndex = 0; fileIndex < filesCollection.length; fileIndex++) {
        multipartFormData.append('files', filesCollection[fileIndex]);
    }
    if (mediaCategory) {
        multipartFormData.append('type', mediaCategory);
    }

    return await DataLayerScratcher.UploadBatchMedia(multipartFormData);
};

/**
 * Displays the clean promotional cover screen on the stadium overlay and screen views.
 * 
 * @async
 * @function ShowCover
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} Response payload.
 */
export const ShowCover = async () => {
    return await DataLayerScratcher.ShowCover();
};

/**
 * Displays the interactive scratch arena stage screen on the stadium overlay.
 * 
 * @async
 * @function ShowGame
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} Response payload.
 */
export const ShowGame = async () => {
    return await DataLayerScratcher.ShowGame();
};

/**
 * Starts a new scratcher match with specified parameters and countdown sequence.
 * 
 * @async
 * @function StartGame
 * @param {number} [durationSeconds=20] - Round duration in seconds.
 * @param {double} [targetPercentage=100] - Percentage target to win by KO.
 * @param {string} [gameMode='simultaneous'] - Game mode ('simultaneous' or 'duel').
 * @param {number} [themeId=null] - Specific theme ID.
 * @param {Array<Object>} [customPlayers=null] - Optional list of extra players.
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} Response payload.
 */
export const StartGame = async (
    durationSeconds = 20,
    targetPercentage = 100,
    gameMode = 'simultaneous',
    themeId = null,
    customPlayers = null,
    coinSize = 25
) => {
    const sanitizedDuration = Number(durationSeconds) > 0 ? Number(durationSeconds) : 20;
    const sanitizedTarget = Number(targetPercentage) > 0 ? Number(targetPercentage) : 100;
    const sanitizedMode = gameMode === 'duel' ? 'duel' : 'simultaneous';
    const sanitizedCoinSize = Number(coinSize) > 0 ? Number(coinSize) : 25;

    return await DataLayerScratcher.StartGame({
        durationSeconds: sanitizedDuration,
        targetPercentage: sanitizedTarget,
        gameMode: sanitizedMode,
        themeId: themeId && Number(themeId) > 0 ? Number(themeId) : null,
        customPlayers: Array.isArray(customPlayers) ? customPlayers : null,
        coinSize: sanitizedCoinSize
    });
};

/**
 * Pauses the active scratcher match.
 * 
 * @async
 * @function PauseGame
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} Response payload.
 */
export const PauseGame = async () => {
    return await DataLayerScratcher.PauseGame();
};

/**
 * Resumes a paused scratcher match.
 * 
 * @async
 * @function ResumeGame
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} Response payload.
 */
export const ResumeGame = async () => {
    return await DataLayerScratcher.ResumeGame();
};

/**
 * Triggers an emergency stop and ends the match immediately.
 * 
 * @async
 * @function FinishEarly
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} Response payload.
 */
export const FinishEarly = async () => {
    return await DataLayerScratcher.FinishEarly();
};

/**
 * Resets the scratcher game state back to idle.
 * 
 * @async
 * @function ResetGame
 * @returns {Promise<{success: boolean, message?: string, state?: Object}>} Response payload.
 */
export const ResetGame = async () => {
    return await DataLayerScratcher.ResetGame();
};

/**
 * Retrieves the current real-time state from the backend engine.
 * 
 * @async
 * @function GetRealtimeState
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload with state data.
 */
export const GetRealtimeState = async () => {
    return await DataLayerScratcher.GetRealtimeState();
};
